import { useState } from 'react'
import { Box, Button, HStack, Spinner, Text, VStack } from '@chakra-ui/react'
import { StatusChip } from '../../shared'
import { formatRelativeTime } from '../../campaign/canvassers/CanvasserSessionRows'
import { useAdminGrantSessions, useAdminRevokeSession } from '../../../hooks/useAdminGrants'
import type { AdminGrantKind } from '../../../types/adminGrant'
import type { GrantSessionRow } from '../../../types/canvasserGrant'

// L11 slice 3b, kind-parameterized in LW-24 Slice 3 Task 3: device rows for one grant (canvasser
// OR attendee) inside the ADMIN table's expand panel. Sibling of
// components/campaign/canvassers/CanvasserSessionRows.tsx (the organizer's campaign-scoped
// version) rather than a generalization of it: the endpoints differ and this one additionally
// renders the session's coarse client-info, added to the shared DTO in Task 1. `kind` selects
// which merged /api/admin/grants/{kind}/... routes the sessions query and the revoke mutation hit.
//
// The per-session (NOT per-panel) pending/failed Sets are carried over deliberately: a single
// scalar meant revoking device B silently wiped device A's still-live failure chip. [LEGAL] Sec.E
// "surface, don't swallow" -- a failed server-side revoke renders as a standing danger row with a
// retry affordance until it succeeds or the admin navigates away.

interface AdminGrantSessionRowsProps {
  kind: AdminGrantKind
  grantId: string
}

function DeviceRow({
  session, onRevoke, isPending, hasFailed,
}: {
  session: GrantSessionRow
  onRevoke: (sessionId: string) => void
  isPending: boolean
  hasFailed: boolean
}) {
  return (
    <VStack align="stretch" gap={2} py={2} borderBottomWidth="1px" borderColor="border">
      <HStack justify="space-between" gap={3} flexWrap="wrap">
        <VStack align="start" gap={0}>
          <Text fontWeight="medium" color={session.revoked ? 'muted' : undefined}>
            {session.deviceLabel ?? 'Unknown device'}{session.revoked ? ' (signed out)' : ''}
          </Text>
          {!!session.clientInfo && (
            <Text fontSize="xs" color="muted">{session.clientInfo}</Text>
          )}
          <Text fontSize="xs" color="muted">
            Last seen {formatRelativeTime(session.lastSeenAt)}
          </Text>
        </VStack>
        {!session.revoked && (
          <Button
            size="sm"
            minH="44px"
            _touch={{ minH: '44px' }}
            variant="outline"
            loading={isPending}
            onClick={() => onRevoke(session.sessionId)}
          >
            Sign out this device
          </Button>
        )}
      </HStack>
      {hasFailed && (
        <HStack gap={2} flexWrap="wrap">
          <StatusChip variant="danger">Server-side revoke FAILED — retry</StatusChip>
          <Button
            size="xs"
            minH="44px"
            _touch={{ minH: '44px' }}
            variant="outline"
            borderColor="danger.fg"
            color="danger.fg"
            loading={isPending}
            onClick={() => onRevoke(session.sessionId)}
          >
            Retry
          </Button>
        </HStack>
      )}
    </VStack>
  )
}

export function AdminGrantSessionRows({ kind, grantId }: AdminGrantSessionRowsProps) {
  const { data: sessions, isLoading } = useAdminGrantSessions(kind, grantId)
  const revokeSession = useAdminRevokeSession(kind)

  const [pendingSessionIds, setPendingSessionIds] = useState<Set<string>>(new Set())
  const [failedSessionIds, setFailedSessionIds] = useState<Set<string>>(new Set())

  const handleRevoke = async (sessionId: string) => {
    setPendingSessionIds((prev) => new Set(prev).add(sessionId))
    // Clear only THIS session's prior failure -- other sessions' chips are untouched.
    setFailedSessionIds((prev) => {
      if (!prev.has(sessionId)) return prev
      const next = new Set(prev)
      next.delete(sessionId)
      return next
    })
    try {
      await revokeSession.mutateAsync({ grantId, sessionId })
    } catch {
      setFailedSessionIds((prev) => new Set(prev).add(sessionId))
    } finally {
      setPendingSessionIds((prev) => {
        const next = new Set(prev)
        next.delete(sessionId)
        return next
      })
    }
  }

  if (isLoading) {
    return (
      <Box px={4} py={6} textAlign="center" bg="surface.2">
        <Spinner size="sm" color="accent" />
      </Box>
    )
  }

  if (!sessions || sessions.length === 0) {
    return (
      <Box px={4} py={4} bg="surface.2">
        <Text fontSize="sm" color="muted">No devices yet.</Text>
      </Box>
    )
  }

  return (
    <Box px={4} py={2} bg="surface.2">
      <VStack align="stretch" gap={0}>
        {sessions.map((s) => (
          <DeviceRow
            key={s.sessionId}
            session={s}
            onRevoke={handleRevoke}
            isPending={pendingSessionIds.has(s.sessionId)}
            hasFailed={failedSessionIds.has(s.sessionId)}
          />
        ))}
      </VStack>
    </Box>
  )
}
